/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React from "react";

interface MediaPrepTransactionTableProps {
  paginatedTransactions: any[];
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number | ((prev: number) => number)) => void;
  filteredTransactions: any[];
  itemsPerPage: number;
  onViewReference?: (refId: string) => void;
  t: any;
}

const MediaPrepTransactionTable: React.FC<MediaPrepTransactionTableProps> = ({
  paginatedTransactions,
  totalPages,
  currentPage,
  setCurrentPage,
  filteredTransactions,
  itemsPerPage,
  onViewReference,
  t
}) => {
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
              <th className="px-8 py-4">{t('mediaPowder.tableColumns.dateTime')}</th>
              <th className="px-8 py-4">{t('mediaPowder.tableColumns.activityProcess')}</th>
              <th className="px-8 py-4">{t('mediaPowder.tableColumns.RefID')}</th>
              <th className="px-8 py-4">{t('mediaPowder.tableColumns.user')}</th>
              <th className="px-8 py-4 text-right">{t('mediaPowder.tableColumns.quantity')}</th>
              <th className="px-8 py-4 text-right">{t('mediaPowder.tableColumns.runningBalance')}</th>
              <th className="px-8 py-4">{t('mediaPowder.tableColumns.remarks')}</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-50">
            {paginatedTransactions.length > 0 ? (
              paginatedTransactions.map((row) => (
                <tr key={row.reference_id} className="hover:bg-slate-50/40 transition-colors text-sm group">
                  <td className="px-8 py-4 font-medium text-slate-600">{row.date_time}</td>

                  <td className="px-8 py-4">
                    <span className={`px-3 py-1 text-[10px] font-black uppercase rounded-full border ${
                      row.activity_type === 'Media Prep Batch'
                        ? 'bg-blue-50 text-blue-600 border-blue-100'
                        : row.activity_type === 'Sample Testing'
                        ? 'bg-amber-50 text-amber-600 border-amber-100'
                        : 'bg-slate-100 text-slate-600 border-slate-200'
                    }`}>
                      {row.activity_type || "-"}
                    </span>
                  </td>

                  <td className="px-8 py-4">
                    <div className="flex justify-center">
                      {row.reference_id ? (
                        <button
                          onClick={() => onViewReference?.(row.reference_id)}
                          className="text-orange-600 font-black hover:underline underline-offset-4 outline-none focus:outline-none focus:ring-0 border-none bg-transparent"
                        >
                          {row.reference_id}
                        </button>
                      ) : (
                        <span className="text-slate-800">-</span>
                      )}
                    </div>
                  </td>

                  <td className="px-8 py-4 font-bold text-slate-700">{row.user_name}</td>

                  <td className={`px-8 py-4 font-black text-right ${
                    row.quantity < 0 ? 'text-red-500' : 'text-emerald-500'
                  }`}>
                    {row.quantity > 0 ? '+' : ''}
                    {Number(row.quantity).toLocaleString()} g
                  </td>

                  <td className="px-8 py-4 font-black text-[#2D3E50] text-right">
                    {Number(row.running_balance).toLocaleString()} g
                  </td>

                  <td className="px-8 py-4 text-slate-500 italic max-w-xs truncate" title={row.remarks || "-"}>
                    {row.remarks || "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-8 py-20 text-center">
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <span className="material-symbols-outlined text-5xl opacity-20">
                      {t('mediaPowder.icons.inventory2')}
                    </span>
                    <p className="text-sm font-bold uppercase tracking-widest">
                      {t('mediaPowder.lotDetails.noUsageRecorded')}
                    </p>
                    <p className="text-xs">
                      {t('mediaPowder.lotDetails.adjustSearch')}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-8 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Showing {((currentPage - 1) * itemsPerPage) + 1}-
            {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length}
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-30"
            >
              <span className="material-symbols-outlined text-lg">
                {t('mediaPowder.icons.chevron_left')}
              </span>
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`w-8 h-8 rounded-lg text-[10px] font-black ${
                  currentPage === i + 1
                    ? 'bg-orange-500 text-white'
                    : 'border border-slate-200 text-slate-400'
                }`}
              >
                {i + 1}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-30"
            >
              <span className="material-symbols-outlined text-lg">
                {t('mediaPowder.icons.chevron_right')}
              </span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MediaPrepTransactionTable;